/** Item-scoped Quantis comments, separate from BOQ review fields and exports. The queries live here, taking the client, so web and native run the same ones. */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '../../../types/generated/database';

/** Any Supabase client on the user's session; RLS on boq_item_comments is the access gate, whichever app holds it. */
export type BoqCommentsDb = SupabaseClient<Database>;
type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];

type CommentRow = Tables<'boq_item_comments'>;

export interface BOQComment {
  id: string;
  itemId: string;
  documentId: string;
  authorId: string;
  authorName: string;
  body: string;
  createdAt: string;
  mentionedUserIds: string[];
}

export interface ListBOQCommentsInput {
  itemId: string;
  documentId: string;
  limit?: number;
  cursor?: string | null;
}

export interface AddBOQCommentInput {
  itemId: string;
  documentId: string;
  body: string;
  mentionedUserIds?: string[];
}

export interface EligibleBOQCommentMention {
  id: string;
  name: string;
}

export interface ListEligibleBOQCommentMentionsInput {
  documentId: string;
  itemId: string;
  query?: string;
}

export interface GetBOQCommentInput {
  documentId: string;
  itemId: string;
  commentId: string;
}

/** Fetch only row identities, never comment bodies, for Pricing row markers.
 * One document-scoped request per page avoids a request for every BOQ row. */
export async function listCommentedBoqItemIds(
  client: BoqCommentsDb,
  documentId: string,
  /** Keep paging until a page comes back empty. A server that caps rows below the page size returns short pages mid-way, and stopping at the first would drop markers. */
  options: { untilEmpty?: boolean } = {},
): Promise<Set<string>> {
  const ids = new Set<string>();
  const pageSize = 1000;
  // Steps by the rows actually received: a server that caps rows below the page size would otherwise skip the rows between.
  let start = 0;
  for (let requests = 0; requests < 10_000; requests += 1) {
    const { data, error } = await client
      .from('boq_item_comments')
      .select('item_id')
      .eq('document_id', documentId)
      .order('id', { ascending: true })
      .range(start, start + pageSize - 1);
    if (error) throw error;
    const page = data ?? [];
    page.forEach(row => ids.add(row.item_id));
    if (options.untilEmpty ? page.length === 0 : page.length < pageSize) break;
    start += options.untilEmpty ? page.length : pageSize;
  }
  return ids;
}

const COMMENT_COLUMNS = 'id, document_id, item_id, author_id, author_name, body, created_at, mentioned_user_ids';
const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 100;

interface CommentCursor {
  createdAt: string;
  id: string;
}

function parseCursor(cursor: string | null | undefined): CommentCursor | null {
  if (cursor == null) return null;
  let value: unknown;
  try {
    value = JSON.parse(cursor);
  } catch {
    throw new Error('Invalid comments cursor');
  }
  if (value === null || typeof value !== 'object') throw new Error('Invalid comments cursor');
  const record = value as Record<string, unknown>;
  const createdAt = record.createdAt;
  const id = record.id;
  if (typeof createdAt !== 'string' || !/^\d{4}-\d\d-\d\dT[\d:.+Z-]+$/.test(createdAt)
    || typeof id !== 'string' || !/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(id)) {
    throw new Error('Invalid comments cursor');
  }
  return { createdAt, id };
}

function pageSize(limit: number | undefined): number {
  if (limit == null) return DEFAULT_LIMIT;
  if (!Number.isInteger(limit) || limit < 1) throw new Error('Invalid comments page size');
  return Math.min(limit, MAX_LIMIT);
}

function mapComment(row: CommentRow): BOQComment {
  return {
    id: row.id,
    itemId: row.item_id,
    documentId: row.document_id,
    authorId: row.author_id,
    authorName: row.author_name || 'Team member',
    body: row.body,
    createdAt: row.created_at,
    mentionedUserIds: row.mentioned_user_ids ?? [],
  };
}

export async function listBoqComments(client: BoqCommentsDb, {
  itemId,
  documentId,
  limit,
  cursor,
}: ListBOQCommentsInput): Promise<{ comments: BOQComment[]; nextCursor: string | null }> {
  const before = parseCursor(cursor);
  const size = pageSize(limit);
  let query = client
    .from('boq_item_comments')
    .select(COMMENT_COLUMNS)
    .eq('document_id', documentId)
    .eq('item_id', itemId)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(size + 1);
  if (before) {
    query = query.or(`created_at.lt.${before.createdAt},and(created_at.eq.${before.createdAt},id.lt.${before.id})`);
  }
  const { data, error } = await query;
  if (error) throw error;

  const rows = (data ?? []) as CommentRow[];
  const page = rows.slice(0, size).reverse();
  return {
    comments: page.map(mapComment),
    nextCursor: rows.length > size && page.length > 0
      ? JSON.stringify({ createdAt: page[0].created_at, id: page[0].id })
      : null,
  };
}

export async function addBoqComment(client: BoqCommentsDb, {
  itemId,
  documentId,
  body,
  mentionedUserIds = [],
}: AddBOQCommentInput): Promise<BOQComment> {
  const text = body.trim();
  const length = Array.from(text).length;
  if (length < 1 || length > 4000) {
    throw new Error('Comment must contain 1 to 4000 characters');
  }
  if (mentionedUserIds.length > 25 || mentionedUserIds.some(id => !/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(id))) {
    throw new Error('Invalid mention recipients');
  }

  // Database default and RLS bind author_id to auth.uid(). No client attribution is sent.
  const { data, error } = await client
    .from('boq_item_comments')
    .insert({ document_id: documentId, item_id: itemId, body: text, mentioned_user_ids: mentionedUserIds })
    .select(COMMENT_COLUMNS)
    .single();
  if (error) throw error;

  const row = data as CommentRow;
  return mapComment(row);
}

/** The RPC checks access to the exact item before returning eligible names. */
export async function listEligibleBoqCommentMentions(client: BoqCommentsDb, {
  documentId, itemId, query = '',
}: ListEligibleBOQCommentMentionsInput): Promise<EligibleBOQCommentMention[]> {
  const { data, error } = await client.rpc('list_eligible_boq_comment_mentions', {
    _document_id: documentId,
    _item_id: itemId,
    _query: query.trim().slice(0, 100),
  });
  if (error) throw error;
  return (data ?? []).map(person => ({ id: person.id, name: person.name }));
}

/** A deep link may target an older comment outside the newest page. */
export async function getBoqComment(client: BoqCommentsDb, {
  documentId, itemId, commentId,
}: GetBOQCommentInput): Promise<BOQComment | null> {
  const { data, error } = await client
    .from('boq_item_comments')
    .select(COMMENT_COLUMNS)
    .eq('document_id', documentId)
    .eq('item_id', itemId)
    .eq('id', commentId)
    .maybeSingle();
  if (error) throw error;
  return data ? mapComment(data as CommentRow) : null;
}
